import type {FullInfo} from '@rocket-ui/lk-api';
import {getResidenceOksmCode, isRussiaCitizenship} from './formatProfile';

const getIdentityVerificationInfo = (fullInfo: FullInfo | undefined) => {
	const info = fullInfo?.info;

	if (!info || typeof info !== 'object') {
		return null;
	}

	return info;
};

/** Баннер «Проверяем ваши документы»: required + status pending. */
export const isIdentityVerificationPending = (fullInfo: FullInfo | undefined): boolean => {
	const info = getIdentityVerificationInfo(fullInfo);

	return info?.identityVerificationRequired === true && info.identityVerificationStatus === 'pending';
};

/** Баннер «Проверка документов не пройдена»: required + status rejected. */
export const isIdentityVerificationRejected = (fullInfo: FullInfo | undefined): boolean => {
	const info = getIdentityVerificationInfo(fullInfo);

	return info?.identityVerificationRequired === true && info.identityVerificationStatus === 'rejected';
};

/** Паспорт РФ: серия и код подразделения есть только у него. */
const RUSSIAN_DOCUMENT_FIELDS = [
	'birthDate',
	'birthPlace',
	'departmentCode',
	'issuedAt',
	'issuer',
	'number',
	'registrationAddress',
	'series',
	'type',
] as const;

/** Иностранный документ: серии нет. Код подразделения — тоже только у паспорта РФ. */
const FOREIGN_DOCUMENT_TYPES = new Set([
	'armenian',
	'belarusian',
	'kazakh',
	'kazakh_id_card',
	'kyrgyz',
	'other',
	'ukraine',
]);

export type CompleteRegistrationEdit = 'personal' | 'passport' | 'inn' | 'statuses';

const isFilled = (value: unknown): boolean =>
	typeof value === 'string' && value.trim().length > 0 && value.trim() !== 'unknown';

const readRecord = (value: unknown): Record<string, unknown> | null => {
	if (!value || typeof value !== 'object') {
		return null;
	}

	return value as Record<string, unknown>;
};

const isForeignDocumentType = (type: unknown): type is string =>
	typeof type === 'string' && FOREIGN_DOCUMENT_TYPES.has(type);

/**
 * Иностранный документ без серии и кода подразделения.
 * Адрес регистрации в РФ нужен только при резидентстве РФ.
 * У удостоверения Казахстана номер лежит в personalNumber.
 */
const isForeignIdentificationDocumentComplete = (
	document: Record<string, unknown>,
	identificationInfo: FullInfo | undefined,
): boolean => {
	const type = document.type;
	const required = ['birthDate', 'birthPlace', 'issuedAt', 'issuer', 'type'];

	if (type === 'kazakh_id_card') {
		const hasNumber =
			isFilled(document.personalNumber) ||
			isFilled(document.identificationNumber) ||
			isFilled(document.number);

		if (!hasNumber) {
			return false;
		}
	} else {
		required.push('number');
	}

	if (isRussiaCitizenship(getResidenceOksmCode(identificationInfo))) {
		required.push('registrationAddress');
	}

	return required.every((field) => isFilled(document[field]));
};

const isIdentificationDocumentComplete = (
	document: Record<string, unknown> | null,
	identificationInfo: FullInfo | undefined,
): boolean => {
	if (!document) {
		return false;
	}

	if (isForeignDocumentType(document.type)) {
		return isForeignIdentificationDocumentComplete(document, identificationInfo);
	}

	return RUSSIAN_DOCUMENT_FIELDS.every((field) => isFilled(document[field]));
};

/**
 * Куда вести с баннера «Не хватает данных».
 * Сначала `/identification/info` (анкета, затем паспорт), потом ИНН из `/profile`,
 * затем статусы — если ни у одного типа занятости нет `active`.
 */
export const resolveCompleteRegistrationEdit = (
	identificationInfo: FullInfo | undefined,
	profile: FullInfo | undefined,
	hasConnectedStatuses: boolean,
): CompleteRegistrationEdit | null => {
	const info = identificationInfo?.info;

	if (
		!isFilled(info?.firstName) ||
		!isFilled(info?.secondName) ||
		!isFilled(info?.gender) ||
		!isFilled(info?.email)
	) {
		return 'personal';
	}

	const document = readRecord(identificationInfo?.identificationDocument);

	if (!isIdentificationDocumentComplete(document, identificationInfo)) {
		return 'passport';
	}

	if (!isFilled(profile?.info?.inn)) {
		return 'inn';
	}

	if (!hasConnectedStatuses) {
		return 'statuses';
	}

	return null;
};

/** Баннер «Не хватает данных», пока не заполнены анкета, паспорт, ИНН или нет `active` у занятости. */
export const isCompleteRegistrationBannerVisible = (
	identificationInfo: FullInfo | undefined,
	profile: FullInfo | undefined,
	hasConnectedStatuses: boolean,
): boolean =>
	resolveCompleteRegistrationEdit(identificationInfo, profile, hasConnectedStatuses) !== null;
